import React, {createContext, useContext, useEffect, useRef, useState} from 'react';
import {Alert} from "react-bootstrap";

const SSEContext = createContext();

export const SseTopics = {
    OnlineList: "online-list",
    Online: "online",
    Schedule: "schedule",
    Upgrade: "upgrade",
    Device: "dev",
    Next: "next",
    Live: "live",
    Network: "net",
    Stream: "stream",
    Codec: "codec",
    WiFi: "wifi",
    RSSI: "rssi",
    RTCC: "rtcc",
    VOL: "vol",
    VU: "vu",
    Relay: "relay",
    Announce: "announce",
    USB: "usb"
}

export const SSEProvider = ({url, topics = Object.values(SseTopics), children}) => {
    const [error, setError] = useState(null)
    const messages = useRef(new Map())
    const subscribers = useRef(new Map())
    const eventSourceRef = useRef(new Set())

    const reconnectSec = 5;

    useEffect(() => {
        if (url) {
            let es = null
            let timeout = null

            function handleEvent(event) {
                const topic = event.type;
                const data = event.data;

                messages.current.set(topic, data)

                if (subscribers.current.has(topic)) {
                    //console.log(data)
                    const cbs = subscribers.current.get(topic)
                    cbs.forEach(cb => cb(data))
                }
            }

            function connect() {
                es = new EventSource(url);

                es.onopen = () => {
                    setError(null)
                    eventSourceRef.current.add(es)
                    topics.forEach(topic => es.addEventListener(topic, handleEvent))
                }


                es.onerror = error => {
                    if (es.readyState === EventSource.CLOSED) {
                        console.log("Connection closed explicitly")
                    } else {
                        console.warn('EventSource failed', error)
                        setError(`SSE closed unexpectedly; reconnect in ${reconnectSec} sec.`)
                        es.close()
                        // Automatically reconnect if connection went down
                        timeout = setTimeout(connect, reconnectSec * 1000)
                    }
                }
            }

            connect()

            return () => {
                clearTimeout(timeout)
                timeout = null
                topics.forEach(topic => es.removeEventListener(topic, handleEvent))
                eventSourceRef.current.delete(es)
                es?.close()
                es = null
            };
        }
    }, [url]);

    function getLatestMessage(topic) {
        return messages.current.get(topic)
    }

    function subscribe(topic, cb) {
        if (!subscribers.current.has(topic)) {
            subscribers.current.set(topic, new Set())
        }
        subscribers.current.get(topic).add(cb)
    }

    function unsubscribe(topic, cb) {
        if (subscribers.current.has(topic)) {
            subscribers.current.get(topic).delete(cb)
        }
    }

    return (
        <SSEContext.Provider value={{getLatestMessage, subscribe, unsubscribe}}>
            <Alert show={error}>{error}</Alert>
            {children}
        </SSEContext.Provider>
    );
};

export const useSSEContext = () => {
    const context = useContext(SSEContext);
    if (!context) {
        throw new Error('useSSEContext must be used within SSEProvider');
    }
    return context;
};
